import { Icon } from '@iconify/react';
import React, { useState, useEffect, useMemo } from 'react';
import config from '../config';
import auth from '../helper/auth';
import { formatDisplayDate } from '../utils/formatDate';

const EmployeeManagementLayer = () => {
    const [employees, setEmployees] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Sorting
    const [sortField, setSortField] = useState('name'); // 'name' | 'role'
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'

    // Form State
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        password: '',
        position_id: ''
    });

    useEffect(() => {
        fetchEmployees();
        fetchPositions();
    }, []);

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/employees`);
            if (!response.ok) throw new Error('Failed to fetch staff members');
            const data = await response.json();
            setEmployees(data);
        } catch (error) {
            console.error('Error fetching staff:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPositions = async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/positions`);
            if (!response.ok) throw new Error('Failed to fetch roles');
            const data = await response.json();
            setPositions(data);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openAddModal = () => {
        setFormData({ full_name: '', email: '', password: '', position_id: '' });
        setIsEditing(false);
        setCurrentId(null);
        setShowModal(true);
    };

    const openEditModal = (emp) => {
        setFormData({
            full_name: emp.full_name,
            email: emp.email,
            password: '',
            position_id: emp.position_id || ''
        });
        setIsEditing(true);
        setCurrentId(emp.id);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.full_name || !formData.email || !formData.position_id) {
            alert('Name, Email and Role are required');
            return;
        }

        if (!isEditing && !formData.password) {
            alert('Password is required for new staff members');
            return;
        }

        try {
            const url = isEditing 
                ? `${config.API_BASE_URL}/api/employees/${currentId}`
                : `${config.API_BASE_URL}/api/employees`;
            
            const method = isEditing ? 'PUT' : 'POST';

            const response = await auth.fetch(url, {
                method: method,
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Operation failed');
            }

            setShowModal(false);
            fetchEmployees();
        } catch (error) {
            console.error('Error saving staff member:', error);
            alert(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) return;

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/employees/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) throw new Error('Failed to delete staff member');

            fetchEmployees();
        } catch (error) {
            console.error('Error deleting staff member:', error);
            alert('Failed to delete staff member');
        }
    };

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    // Filter & Sort
    const processedEmployees = useMemo(() => {
        let list = employees.filter(emp => 
            (emp.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.position_title && emp.position_title.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        list.sort((a, b) => {
            let valA = sortField === 'name' ? (a.full_name || '') : (a.position_title || '');
            let valB = sortField === 'name' ? (b.full_name || '') : (b.position_title || '');
            const cmp = valA.localeCompare(valB);
            return sortOrder === 'asc' ? cmp : -cmp;
        });

        return list;
    }, [employees, searchTerm, sortField, sortOrder]);

    return (
        <div className="card h-100 p-0 radius-12 shadow-sm border-0">
            {/* Header */}
            <div className="card-header border-bottom bg-white py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div>
                    <h6 className="text-lg fw-bold mb-0 text-dark">Staff Management</h6>
                    <span className="text-xs text-secondary-light">Manage platform staff members, role assignments, and credentials</span>
                </div>
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <div className="hs-search-wrapper" style={{ maxWidth: '240px' }}>
                        <Icon icon="solar:magnifer-linear" className="hs-search-icon" />
                        <input 
                            type="text" 
                            className="hs-input" 
                            placeholder="Search staff..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button 
                        onClick={openAddModal}
                        className="btn btn-sm text-white radius-8 d-flex align-items-center gap-1.5 shadow-none"
                        style={{ backgroundColor: '#0388d6', border: 'none' }}
                    >
                        <Icon icon="solar:user-plus-bold" className="text-lg" />
                        <span>Add Staff Member</span>
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="card-body p-0">
                {loading ? (
                    <div className="text-center p-5 text-secondary-light">Loading staff directory...</div>
                ) : (
                    <div className="table-responsive">
                        <table className="hs-table mb-0">
                            <thead>
                                <tr>
                                    <th 
                                        scope="col" 
                                        onClick={() => toggleSort('name')}
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        title="Click to sort by Name"
                                    >
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Name</span>
                                            {sortField === 'name' && (
                                                <Icon icon={sortOrder === 'asc' ? 'solar:arrow-up-linear' : 'solar:arrow-down-linear'} className="text-primary-600" />
                                            )}
                                        </div>
                                    </th>
                                    <th scope="col">Email</th>
                                    <th 
                                        scope="col"
                                        onClick={() => toggleSort('role')}
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        title="Click to sort by Role"
                                    >
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Role</span>
                                            {sortField === 'role' && (
                                                <Icon icon={sortOrder === 'asc' ? 'solar:arrow-up-linear' : 'solar:arrow-down-linear'} className="text-primary-600" />
                                            )}
                                        </div>
                                    </th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Created At</th>
                                    <th scope="col" className="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {processedEmployees.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center p-4 text-secondary-light">No staff members found.</td>
                                    </tr>
                                ) : processedEmployees.map(emp => (
                                    <tr key={emp.id}>
                                        <td className="fw-medium">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-flex align-items-center justify-content-center fw-bold border border-primary-100">
                                                    {(emp.full_name || 'S').charAt(0).toUpperCase()}
                                                </div>
                                                <span className="text-dark fw-semibold text-sm">{emp.full_name}</span>
                                            </div>
                                        </td>
                                        <td className="text-sm text-secondary-dark">{emp.email}</td>
                                        <td>
                                            {emp.position_title ? (
                                                <span className="badge bg-neutral-100 text-neutral-800 border px-2.5 py-1 radius-pill fw-medium text-xs">
                                                    {emp.position_title}
                                                </span>
                                            ) : (
                                                <span className="text-muted text-xs">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`hs-badge ${emp.status === 'active' ? 'hs-badge-success' : 'hs-badge-danger'}`}>
                                                {emp.status}
                                            </span>
                                        </td>
                                        <td className="text-secondary-light text-xs">{formatDisplayDate(emp.created_at)}</td>
                                        <td className="text-end">
                                            <div className="d-inline-flex align-items-center gap-1">
                                                <button 
                                                    onClick={() => openEditModal(emp)}
                                                    className="hs-icon-btn hs-btn-sm"
                                                    title="Edit Staff Member"
                                                >
                                                    <Icon icon="solar:pen-bold" className="text-primary-600" />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(emp.id)}
                                                    className="hs-icon-btn hs-btn-sm text-danger-600"
                                                    title="Delete Staff Member"
                                                >
                                                    <Icon icon="solar:trash-bin-trash-bold" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header border-bottom">
                                <h5 className="modal-title fw-bold text-dark">{isEditing ? 'Edit Staff Member' : 'Add Staff Member'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-24">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-xs text-dark">Full Name <span className="text-danger">*</span></label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                name="full_name" 
                                                value={formData.full_name} 
                                                onChange={handleInputChange} 
                                                placeholder="e.g. John Doe"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-xs text-dark">Email Address <span className="text-danger">*</span></label>
                                            <input 
                                                type="email" 
                                                className="form-control" 
                                                name="email" 
                                                value={formData.email} 
                                                onChange={handleInputChange} 
                                                placeholder="john@hubscroll.com"
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-xs text-dark">Role <span className="text-danger">*</span></label>
                                            <select 
                                                className="form-select" 
                                                name="position_id" 
                                                value={formData.position_id} 
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Role</option>
                                                {positions.map(pos => (
                                                    <option key={pos.id} value={pos.id}>{pos.title}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-xs text-dark">
                                                {isEditing ? 'New Password (Optional)' : 'Password'} 
                                                {!isEditing && <span className="text-danger">*</span>}
                                            </label>
                                            <input 
                                                type="password" 
                                                className="form-control" 
                                                name="password" 
                                                value={formData.password} 
                                                onChange={handleInputChange} 
                                                placeholder={isEditing ? "Leave blank to keep current" : "Enter secure password"}
                                                required={!isEditing}
                                                minLength="6"
                                            />
                                            {isEditing && <div className="form-text text-xs">Only enter if you want to reset password.</div>}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <button type="button" className="hs-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                        <button 
                                            type="submit" 
                                            className="btn btn-sm text-white px-3 py-2 radius-8 fw-semibold shadow-none"
                                            style={{ backgroundColor: '#0388d6', border: 'none' }}
                                        >
                                            {isEditing ? 'Update Staff Member' : 'Create Staff Member'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeManagementLayer;
